import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { MdAdd, MdArrowBack, MdDelete } from "react-icons/md";
import Card from "components/card";
import ActionButton from "components/ui/ActionButton";
import { useLanguage } from "context/LanguageContext";
import { classMgmtService } from "services/admin/classMgmtService";
import { userService } from "services/admin/userService";
import { venueService } from "services/admin/venueService";
import { hasVietmapServiceKey } from "services/vietmapService";
import type {
  ClassEnrollment,
  ClassItem,
  UserListItem,
  VenueItem,
} from "types/domain";
import { useVenueAddressSuggestions } from "./hooks/useVenueAddressSuggestions";
import {
  useSessionParticipantOverlap,
  type OverlapInfo,
} from "./hooks/useSessionParticipantOverlap";
import {
  buildSessionTitle,
  composeLocalDateTime,
  formatCoordinate,
  parseLocation,
  toFriendlyError,
  toInputDate,
  toInputTime,
} from "./utils/sessionHelpers";
import ParticipantPickerModal from "./components/ParticipantPickerModal";
import SessionStudentPicker from "./components/SessionStudentPicker";
import SelectedParticipantCard from "./components/SelectedParticipantCard";
import VenueMapPicker from "./components/VenueMapPicker";

const formatDob = (
  value: string | null | undefined,
  t: (key: string) => string,
  locale: string
) => {
  if (!value) return t("sessionEditor.dobMissing");
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return t("sessionEditor.dobMissing");
  return parsed.toLocaleDateString(locale);
};

const SessionEditor = () => {
  const { id, sessionId } = useParams<{ id: string; sessionId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = Boolean(sessionId);
  const { t, locale } = useLanguage();
  const formatDobLabel = (value?: string | null) => formatDob(value, t, locale);

  const prefillParams = React.useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const prefillStart = prefillParams.get("start");
  const prefillEnd = prefillParams.get("end");
  const prefillDuration = prefillParams.get("duration");

  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [creatingVenue, setCreatingVenue] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const [classData, setClassData] = React.useState<ClassItem | null>(null);
  const [enrollments, setEnrollments] = React.useState<ClassEnrollment[]>([]);
  const [students, setStudents] = React.useState<UserListItem[]>([]);
  const [managers, setManagers] = React.useState<UserListItem[]>([]);
  const [trainers, setTrainers] = React.useState<UserListItem[]>([]);
  const [venues, setVenues] = React.useState<VenueItem[]>([]);

  const [sessionTeacherIds, setSessionTeacherIds] = React.useState<string[]>(
    []
  );
  const [sessionTrainerIds, setSessionTrainerIds] = React.useState<string[]>(
    []
  );
  const [sessionStudentIds, setSessionStudentIds] = React.useState<string[]>(
    []
  );
  const [sessionStudentSearch, setSessionStudentSearch] = React.useState("");

  const [showCreateVenueForm, setShowCreateVenueForm] = React.useState(false);
  const [venueForm, setVenueForm] = React.useState({
    name: "",
    address: "",
    description: "",
    latitude: 10.776889,
    longitude: 106.700806,
  });

  const [form, setForm] = React.useState({
    date: "",
    startTime: "",
    endTime: "",
    allowOutsideCheckin: false,
    venueId: "",
    durationMinutes: "90",
  });

  React.useEffect(() => {
    const startDate = composeLocalDateTime(form.date, form.startTime);
    const endDate = composeLocalDateTime(form.date, form.endTime);
    if (!startDate || !endDate) return;

    const startMs = startDate.getTime();
    const endMs = endDate.getTime();
    if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs <= startMs)
      return;

    const computedDuration = String(
      Math.max(1, Math.round((endMs - startMs) / 60000))
    );
    setForm((prev) =>
      prev.durationMinutes === computedDuration
        ? prev
        : { ...prev, durationMinutes: computedDuration }
    );
  }, [form.date, form.endTime, form.startTime]);

  const handleAddressPreview = React.useCallback(
    (suggestion: { display: string }) => {
      setVenueForm((prev) => ({
        ...prev,
        address: suggestion.display,
      }));
    },
    []
  );

  const handleAddressResolved = React.useCallback(
    (payload: { address: string; latitude: number; longitude: number }) => {
      setVenueForm((prev) => ({
        ...prev,
        address: payload.address,
        latitude: payload.latitude,
        longitude: payload.longitude,
      }));
    },
    []
  );

  const {
    addressSuggestions,
    addressSuggestLoading,
    addressSuggestError,
    handleSelectAddressSuggestion,
  } = useVenueAddressSuggestions({
    enabled: showCreateVenueForm,
    keyword: venueForm.address,
    latitude: venueForm.latitude,
    longitude: venueForm.longitude,
    onSuggestionPreview: handleAddressPreview,
    onSuggestionResolved: handleAddressResolved,
  });

  const loadUsersByRole = React.useCallback(
    async (
      role: "STUDENT" | "TRAINER" | "TEACHER" | "MANAGER",
      centerId?: string | null
    ) => {
      const limit = 200;
      let skip = 0;
      let allItems: UserListItem[] = [];

      while (true) {
        const res = await userService.getAll({
          role,
          center_id: centerId ?? undefined,
          skip,
          limit,
        });
        allItems = [...allItems, ...res.items];
        if (res.items.length < limit) break;
        skip += limit;
      }

      return allItems;
    },
    []
  );

  const loadVenues = React.useCallback(async (centerId?: string | null) => {
    const response = await venueService.getAll({
      center_id: centerId ?? undefined,
      limit: 200,
    });
    return response.items;
  }, []);

  const currentDateWindow = React.useMemo(() => {
    const startDate = composeLocalDateTime(form.date, form.startTime);
    const endDate = composeLocalDateTime(form.date, form.endTime);
    if (!startDate || !endDate || endDate.getTime() <= startDate.getTime()) {
      return null;
    }
    return {
      from: startDate.toISOString(),
      to: endDate.toISOString(),
    };
  }, [form.date, form.startTime, form.endTime]);

  React.useEffect(() => {
    if (!id) return;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [classItem, classSessions, classEnrollments] = await Promise.all([
          classMgmtService.getById(id),
          classMgmtService.getSessions(id),
          classMgmtService.getEnrollments(id),
        ]);

        const [trainerItems, teacherItems, managerItems, studentItems] =
          await Promise.all([
            loadUsersByRole("TRAINER", classItem.center_id),
            loadUsersByRole("TEACHER", classItem.center_id),
            loadUsersByRole("MANAGER", classItem.center_id),
            loadUsersByRole("STUDENT", classItem.center_id),
          ]);

        setClassData(classItem);
        setEnrollments(classEnrollments);
        // Class managers: TEACHER + MANAGER
        const mergedManagerMap = new Map<string, UserListItem>();
        [...teacherItems, ...managerItems].forEach((user) =>
          mergedManagerMap.set(user.id, user)
        );
        setManagers(Array.from(mergedManagerMap.values()));
        setStudents(studentItems);

        // Trainers: TRAINER + TEACHER + MANAGER
        const mergedTrainerMap = new Map<string, UserListItem>();
        [...trainerItems, ...teacherItems, ...managerItems].forEach((user) =>
          mergedTrainerMap.set(user.id, user)
        );
        setTrainers(Array.from(mergedTrainerMap.values()));

        const venueItems = await loadVenues(classItem.center_id);
        setVenues(venueItems);

        const now = new Date();
        const parsedPrefillStart = prefillStart ? new Date(prefillStart) : null;
        const parsedPrefillEnd = prefillEnd ? new Date(prefillEnd) : null;

        const initialStart =
          parsedPrefillStart && !Number.isNaN(parsedPrefillStart.getTime())
            ? parsedPrefillStart
            : now;
        const initialEnd =
          parsedPrefillEnd && !Number.isNaN(parsedPrefillEnd.getTime())
            ? parsedPrefillEnd
            : new Date(initialStart.getTime() + 90 * 60 * 1000);

        const resolvedDuration = Math.max(
          1,
          Number(prefillDuration) ||
            Math.round(
              (initialEnd.getTime() - initialStart.getTime()) / 60000
            ) ||
            90
        );

        const targetSession = sessionId
          ? classSessions.find((session) => session.id === sessionId)
          : null;

        setForm({
          date: targetSession?.start_time
            ? toInputDate(new Date(targetSession.start_time))
            : toInputDate(initialStart),
          startTime: targetSession?.start_time
            ? toInputTime(new Date(targetSession.start_time))
            : toInputTime(initialStart),
          endTime: targetSession?.end_time
            ? toInputTime(new Date(targetSession.end_time))
            : toInputTime(initialEnd),
          allowOutsideCheckin: targetSession?.allow_outside_checkin ?? false,
          venueId: targetSession?.venue_id ?? "",
          durationMinutes: String(
            targetSession?.duration_minutes ?? resolvedDuration
          ),
        });

        if (targetSession) {
          const participants = await classMgmtService.getSessionParticipants(
            targetSession.id
          );
          const enrollmentByStudentId = new Map(
            classEnrollments.map((enrollment) => [
              enrollment.student_id,
              enrollment,
            ])
          );
          const teacherIds = participants
            .filter((participant) => participant.role === "teacher")
            .map((participant) => participant.user_id);
          const trainerIds = participants
            .filter((participant) => participant.role === "trainer")
            .map((participant) => participant.user_id);
          const studentIds = participants
            .filter((participant) => participant.role === "student")
            .map((participant) => participant.user_id)
            .filter((studentId) => {
              const enrollment = enrollmentByStudentId.get(studentId);
              return enrollment?.remaining_sessions !== 0;
            });
          setSessionTeacherIds(teacherIds);
          setSessionTrainerIds(trainerIds);
          setSessionStudentIds(studentIds);
        } else {
          setSessionTeacherIds([]);
          setSessionTrainerIds([]);
          setSessionStudentIds([]);
        }
      } catch (err: any) {
        setError(err.message ?? t("sessionEditor.loadFailed"));
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [
    id,
    loadUsersByRole,
    loadVenues,
    prefillDuration,
    prefillEnd,
    prefillStart,
    sessionId,
  ]);

  const {
    enrolledStudents,
    loadingOverlapCheck,
    managerOverlapMap,
    studentOverlapMap,
    trainerOverlapMap,
    participantPicker,
    teacherSearch,
    trainerSearch,
    setTeacherSearch,
    setTrainerSearch,
    filteredManagerOptions,
    filteredTrainerOptions,
    invalidRoleOverlapIds,
    selectedScheduleConflictUsers,
    selectedManagerConflictUsers,
    handleOpenParticipantPicker,
    closeParticipantPicker,
    recheckSelectedParticipantsBeforeSubmit,
  } = useSessionParticipantOverlap({
    currentDateWindow,
    isEdit,
    sessionId,
    students,
    managers,
    trainers,
    enrollments,
    sessionTeacherIds,
    sessionTrainerIds,
    sessionStudentIds,
    setSessionTrainerIds,
    setSessionStudentIds,
  });

  const selectedVenue = React.useMemo(
    () => venues.find((venue) => venue.id === form.venueId) ?? null,
    [venues, form.venueId]
  );

  const selectedVenueLocation = React.useMemo(
    () => parseLocation(selectedVenue?.location),
    [selectedVenue]
  );

  const selectedTeachers = React.useMemo(
    () => managers.filter((manager) => sessionTeacherIds.includes(manager.id)),
    [managers, sessionTeacherIds]
  );

  const selectedTrainers = React.useMemo(
    () => trainers.filter((trainer) => sessionTrainerIds.includes(trainer.id)),
    [trainers, sessionTrainerIds]
  );

  const userLookup = React.useMemo(() => {
    const map = new Map<string, UserListItem>();
    [...students, ...managers, ...trainers].forEach((user) =>
      map.set(user.id, user)
    );
    return map;
  }, [students, managers, trainers]);

  const enrollmentByStudentId = React.useMemo(() => {
    const map = new Map<string, ClassEnrollment>();
    enrollments.forEach((enrollment) =>
      map.set(enrollment.student_id, enrollment)
    );
    return map;
  }, [enrollments]);

  const handleCreateVenue = async () => {
    if (!venueForm.name.trim() || !venueForm.address.trim()) {
      alert(t("venue.nameAndAddressRequired"));
      return;
    }

    if (!classData?.center_id) {
      alert(t("venue.unknownCenter"));
      return;
    }

    setCreatingVenue(true);
    try {
      const createdVenue = await venueService.create({
        center_id: classData.center_id,
        name: venueForm.name.trim(),
        address: venueForm.address.trim(),
        description: venueForm.description.trim() || undefined,
        location: `${venueForm.latitude.toFixed(
          6
        )},${venueForm.longitude.toFixed(6)}`,
      });
      setVenues((prev) => [createdVenue, ...prev]);
      setForm((prev) => ({ ...prev, venueId: createdVenue.id }));
      setShowCreateVenueForm(false);
    } catch (err: any) {
      alert(err.message ?? t("venue.createFailed"));
    } finally {
      setCreatingVenue(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!id) return;

    if (!form.venueId) {
      alert(t("sessionEditor.venueHint"));
      return;
    }

    if (!form.date || !form.startTime || !form.endTime) {
      alert(t("sessionEditor.dateTimeRequired"));
      return;
    }

    const startDate = composeLocalDateTime(form.date, form.startTime);
    const endDate = composeLocalDateTime(form.date, form.endTime);

    if (!startDate || !endDate) {
      alert(t("sessionEditor.dateTimeInvalid"));
      return;
    }

    if (endDate.getTime() <= startDate.getTime()) {
      alert(t("sessionEditor.endBeforeStart"));
      return;
    }

    if (!isEdit && startDate.getTime() < Date.now()) {
      alert(t("sessionEditor.startInPast"));
      return;
    }

    if (invalidRoleOverlapIds.length > 0) {
      const duplicatedUsers = invalidRoleOverlapIds
        .map((userId) => userLookup.get(userId)?.full_name ?? userId)
        .join("\n- ");
      alert(t("sessionEditor.duplicateRolesAlert", { users: duplicatedUsers }));
      return;
    }

    setSaving(true);
    try {
      let overlapRecheckResult: {
        trainerMap: Map<string, OverlapInfo>;
        studentMap: Map<string, OverlapInfo>;
      };

      overlapRecheckResult = await recheckSelectedParticipantsBeforeSubmit();

      const { trainerMap, studentMap } = overlapRecheckResult;
      const hasOverlapAfterRecheck = trainerMap.size > 0 || studentMap.size > 0;

      if (hasOverlapAfterRecheck) {
        setSessionTrainerIds((prev) =>
          prev.filter((userId) => !trainerMap.has(userId))
        );
        setSessionStudentIds((prev) =>
          prev.filter((userId) => !studentMap.has(userId))
        );

        const conflictMessages = Array.from(
          new Map<string, OverlapInfo>([...trainerMap, ...studentMap]).values()
        ).map((item) => `${item.userName}: ${item.sessionNames.join(", ")}`);

        alert(
          t("sessionEditor.conflictOnSaveAlert", {
            list: conflictMessages.join("\n- "),
          })
        );
        return;
      }

      const computedDuration =
        startDate && endDate
          ? Math.round((endDate.getTime() - startDate.getTime()) / 60000)
          : Number(form.durationMinutes);
      const durationMinutes = Math.max(1, computedDuration || 90);
      const payload = {
        venue_id: form.venueId || null,
        allow_outside_checkin: form.allowOutsideCheckin,
        duration_minutes: durationMinutes,
        start_time: startDate.toISOString(),
        end_time: endDate.toISOString(),
      };

      let targetSessionId = sessionId ?? "";

      if (sessionId) {
        await classMgmtService.updateSession(sessionId, payload);
      } else {
        if (!payload.venue_id) {
          alert(t("sessionEditor.venueHint"));
          return;
        }
        const generatedTitle = buildSessionTitle(classData?.name);
        const created = await classMgmtService.createSession({
          title: generatedTitle,
          session_type: "CLASS",
          class_id: id,
          venue_id: payload.venue_id ?? undefined,
          allow_outside_checkin: payload.allow_outside_checkin,
          start_time: payload.start_time,
          end_time: payload.end_time,
          duration_minutes: durationMinutes,
        });
        targetSessionId = created.id;
      }

      if (targetSessionId) {
        try {
          await classMgmtService.syncSessionParticipants(targetSessionId, {
            teacher_ids: sessionTeacherIds,
            teacher_id: sessionTeacherIds[0] ?? null,
            trainer_ids: sessionTrainerIds,
            trainer_id: sessionTrainerIds[0] ?? null,
            student_ids: sessionStudentIds,
          });
        } catch (syncErr: any) {
          alert(
            toFriendlyError(
              syncErr,
              t("sessionEditor.syncParticipantsFailed"),
              t
            )
          );
        }
      }

      navigate(`/admin/classes-mgmt/${id}`);
    } catch (err: any) {
      alert(toFriendlyError(err, t("sessionEditor.saveFailed"), t));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSession = async () => {
    if (!sessionId) return;
    if (!window.confirm(t("sessionEditor.deleteConfirm"))) return;

    setSaving(true);
    try {
      await classMgmtService.deleteSession(sessionId);
      navigate(`/admin/classes-mgmt/${id}`);
    } catch (err: any) {
      alert(err.message ?? t("sessionEditor.deleteFailed"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="mt-5 text-sm text-ink-muted">
        {t("sessionEditor.loading")}
      </div>
    );
  }

  if (error || !classData) {
    return (
      <div className="mt-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/10 dark:text-red-400">
        {error ?? t("sessionEditor.classNotFound")}
      </div>
    );
  }

  return (
    <div className="mt-5 space-y-5">
      <div className="flex items-center gap-3">
        <ActionButton
          variant="ghost"
          size="sm"
          onClick={() =>
            navigate(
              isEdit
                ? `/admin/classes-mgmt/${id}/sessions/${sessionId}`
                : `/admin/classes-mgmt/${id}`
            )
          }
          className="rounded-lg border border-line p-2 text-ink-muted dark:hover:bg-surface-hover"
        >
          <MdArrowBack className="h-5 w-5" />
        </ActionButton>
        <div>
          <h2 className="text-2xl font-bold text-ink">
            {isEdit
              ? t("sessionEditor.editTitle")
              : t("sessionEditor.createTitle")}
          </h2>
          <p className="text-sm text-ink-muted">
            {t("sessionEditor.classLine", { name: classData.name })}
          </p>
        </div>
      </div>

      <Card extra="p-6">
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
            <div className="lg:col-span-3">
              <label className="mb-1.5 block text-sm font-medium text-ink-muted">
                {t("sessionEditor.dateLabel")}
              </label>
              <input
                type="date"
                required
                value={form.date}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, date: event.target.value }))
                }
                className="bg-transparent w-full rounded-lg border border-line px-4 py-2.5 text-sm text-ink outline-none focus:border-brand-500 dark:bg-surface"
              />
            </div>

            <div className="lg:col-span-5">
              <label className="mb-1.5 block text-sm font-medium text-ink-muted">
                {t("sessionEditor.timeLabel")}
              </label>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="time"
                  required
                  value={form.startTime}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      startTime: event.target.value,
                    }))
                  }
                  className="bg-transparent w-full rounded-lg border border-line px-3 py-2.5 text-sm text-ink outline-none focus:border-brand-500 dark:bg-surface"
                />
                <input
                  type="time"
                  required
                  value={form.endTime}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      endTime: event.target.value,
                    }))
                  }
                  className="bg-transparent w-full rounded-lg border border-line px-3 py-2.5 text-sm text-ink outline-none focus:border-brand-500 dark:bg-surface"
                />
              </div>
            </div>

            <div className="lg:col-span-4">
              <label className="mb-1.5 block text-sm font-medium text-ink-muted">
                {t("sessionEditor.outsideCheckinLabel")}
              </label>
              <div className="flex items-center gap-2 py-1">
                <button
                  type="button"
                  role="switch"
                  aria-checked={form.allowOutsideCheckin}
                  onClick={() => {
                    const checked = !form.allowOutsideCheckin;
                    setForm((prev) => ({
                      ...prev,
                      allowOutsideCheckin: checked,
                    }));
                  }}
                  className={`relative inline-flex h-6 w-10 items-center rounded-full transition ${
                    form.allowOutsideCheckin
                      ? "bg-green-500"
                      : "bg-gray-300 dark:bg-surface-hover"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                      form.allowOutsideCheckin
                        ? "translate-x-5"
                        : "translate-x-1"
                    }`}
                  />
                </button>

                <span
                  className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                    form.allowOutsideCheckin
                      ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
                      : "bg-gray-200 text-gray-600 dark:bg-surface-muted dark:text-gray-300"
                  }`}
                >
                  {form.allowOutsideCheckin
                    ? t("sessionEditor.toggleOn")
                    : t("sessionEditor.toggleOff")}
                </span>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-line bg-gray-50/70 p-4 dark:bg-surface-muted/20">
            <div className="mb-3 flex items-center justify-between">
              <h5 className="text-sm font-semibold text-ink">
                {t("sessionEditor.participantsTitle")}
              </h5>
              <span className="rounded-full bg-surface px-2.5 py-1 text-xs font-semibold text-gray-600 dark:text-gray-300">
                {t("sessionEditor.studentCount", {
                  count: sessionStudentIds.length,
                })}
              </span>
            </div>

            {(loadingOverlapCheck ||
              invalidRoleOverlapIds.length > 0 ||
              selectedManagerConflictUsers.length > 0 ||
              selectedScheduleConflictUsers.length > 0) && (
              <div className="mb-3 space-y-2">
                {loadingOverlapCheck && (
                  <p className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-700 dark:border-blue-900/40 dark:bg-blue-900/20 dark:text-blue-300">
                    {t("sessionEditor.checkingOverlap")}
                  </p>
                )}
                {invalidRoleOverlapIds.length > 0 && (
                  <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-300">
                    {t("sessionEditor.duplicateRolesWarning")}
                  </p>
                )}
                {selectedManagerConflictUsers.length > 0 && (
                  <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-300">
                    {t("sessionEditor.managerConflictWarning", {
                      count: selectedManagerConflictUsers.length,
                    })}
                  </p>
                )}
                {selectedScheduleConflictUsers.length > 0 && (
                  <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300">
                    {t("sessionEditor.scheduleConflictWarning", {
                      count: selectedScheduleConflictUsers.length,
                    })}
                  </p>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              <div className="space-y-3 rounded-2xl border border-line bg-surface p-4">
                <div className="flex items-center justify-between gap-3">
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                    {t("enum.role.TEACHER")}
                  </label>
                  <ActionButton
                    variant="secondary"
                    size="sm"
                    onClick={() => void handleOpenParticipantPicker("teacher")}
                    startIcon={<MdAdd className="h-3.5 w-3.5" />}
                    className="rounded-lg border-brand-200 px-2.5 py-1 text-xs dark:border-brand-900/40"
                  >
                    {t("sessionEditor.addManager")}
                  </ActionButton>
                </div>

                {selectedTeachers.length > 0 ? (
                  <div className="space-y-2">
                    {selectedTeachers.map((manager) => (
                      <SelectedParticipantCard
                        key={manager.id}
                        user={manager}
                        label={t("sessionEditor.inChargeBadge")}
                        variant="primary"
                        formatDob={formatDobLabel}
                        helperText={
                          managerOverlapMap.get(manager.id)
                            ? t("sessionEditor.overlapsWith", {
                                sessions:
                                  managerOverlapMap
                                    .get(manager.id)
                                    ?.sessionNames.join(", ") ?? "",
                              })
                            : null
                        }
                      />
                    ))}
                  </div>
                ) : (
                  <p className="rounded-xl border border-dashed border-line px-3 py-4 text-center text-xs text-ink-muted">
                    {t("sessionEditor.noManagerSelected")}
                  </p>
                )}
              </div>

              <div className="space-y-3 rounded-2xl border border-line bg-surface p-4">
                <div className="flex items-center justify-between gap-3">
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                    {t("enum.role.TRAINER")}
                  </label>
                  <ActionButton
                    variant="secondary"
                    size="sm"
                    onClick={() => void handleOpenParticipantPicker("trainer")}
                    startIcon={<MdAdd className="h-3.5 w-3.5" />}
                    className="rounded-lg border-brand-200 px-2.5 py-1 text-xs dark:border-brand-900/40"
                  >
                    {t("sessionEditor.addTrainer")}
                  </ActionButton>
                </div>

                {selectedTrainers.length > 0 ? (
                  <div className="space-y-2">
                    {selectedTrainers.map((trainer) => (
                      <SelectedParticipantCard
                        key={trainer.id}
                        user={trainer}
                        label={t("enum.role.TRAINER")}
                        variant="neutral"
                        formatDob={formatDobLabel}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="rounded-xl border border-dashed border-line px-3 py-4 text-center text-xs text-ink-muted">
                    {t("sessionEditor.noTrainerSelected")}
                  </p>
                )}
              </div>
            </div>

            <SessionStudentPicker
              sessionStudentSearch={sessionStudentSearch}
              onSearchChange={setSessionStudentSearch}
              students={enrolledStudents}
              sessionStudentIds={sessionStudentIds}
              studentOverlapMap={studentOverlapMap}
              enrollmentByStudentId={enrollmentByStudentId}
              setSessionStudentIds={setSessionStudentIds}
              formatDob={formatDobLabel}
            />
          </div>

          <div className="rounded-2xl border border-line bg-gray-50/50 p-4 dark:bg-surface-muted/20">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <label className="text-sm font-semibold text-ink">
                  {t("sessionEditor.venueTitle")}
                </label>
                <p className="mt-0.5 text-xs text-ink-muted">
                  {t("sessionEditor.venueHint")}
                </p>
              </div>
              <ActionButton
                variant="secondary"
                size="sm"
                onClick={() => setShowCreateVenueForm((prev) => !prev)}
                className="rounded-lg border-brand-200 px-2.5 py-1 text-xs dark:border-brand-900/30"
              >
                {showCreateVenueForm
                  ? t("venue.hideCreateForm")
                  : t("venue.showCreateForm")}
              </ActionButton>
            </div>

            <div className="space-y-3">
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                {t("sessionEditor.venueSelectLabel")}
              </label>
              <div className="rounded-xl border border-line bg-surface px-3 py-2">
                <select
                  value={form.venueId}
                  onChange={(e) => {
                    const venueId = e.target.value;
                    setForm((prev) => ({ ...prev, venueId }));
                    const nextSelectedVenue = venues.find(
                      (venue) => venue.id === venueId
                    );
                    const parsedLocation = parseLocation(
                      nextSelectedVenue?.location
                    );
                    if (parsedLocation) {
                      setVenueForm((prev) => ({
                        ...prev,
                        latitude: parsedLocation.lat,
                        longitude: parsedLocation.lng,
                      }));
                    }
                  }}
                  className="bg-transparent w-full border-0 px-0 py-0.5 text-sm font-medium text-ink outline-none focus:ring-0"
                >
                  <option value="">{t("venue.selectPlaceholder")}</option>
                  {venues.map((venue) => (
                    <option key={venue.id} value={venue.id}>
                      {venue.name}
                      {venue.address ? ` - ${venue.address}` : ""}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {selectedVenue && (
              <div className="mt-3 rounded-xl border border-line bg-white p-3 dark:bg-surface-muted/40">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-ink">
                      {selectedVenue.name}
                    </p>
                    <p className="mt-0.5 text-xs text-ink-muted">
                      {selectedVenue.address
                        ? selectedVenue.address
                        : t("venue.noAddress")}
                    </p>
                  </div>
                  {selectedVenueLocation && (
                    <span className="rounded-full bg-surface-muted px-2.5 py-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                      {t("venue.coordinates", {
                        lat: formatCoordinate(selectedVenueLocation.lat),
                        lng: formatCoordinate(selectedVenueLocation.lng),
                      })}
                    </span>
                  )}
                </div>

                {!selectedVenueLocation ? (
                  <p className="mt-3 rounded-lg border border-dashed border-line px-3 py-2 text-xs text-ink-muted">
                    {t("venue.noCoordinates")}
                  </p>
                ) : null}
              </div>
            )}

            {showCreateVenueForm && (
              <div className="mt-4 space-y-3 rounded-xl border border-line bg-gray-50/60 p-4 dark:bg-surface-muted/30">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                      {t("venue.nameLabel")}
                    </label>
                    <input
                      type="text"
                      value={venueForm.name}
                      onChange={(e) =>
                        setVenueForm((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                      placeholder={t("venue.namePlaceholder")}
                      className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none placeholder:text-gray-400 focus:border-brand-500"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                      {t("venue.addressLabel")}
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={venueForm.address}
                        onChange={(e) =>
                          setVenueForm((prev) => ({
                            ...prev,
                            address: e.target.value,
                          }))
                        }
                        placeholder={t("venue.addressPlaceholder")}
                        className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none placeholder:text-gray-400 focus:border-brand-500"
                      />

                      {showCreateVenueForm &&
                      venueForm.address.trim().length >= 2 ? (
                        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-line bg-surface shadow-lg">
                          {addressSuggestLoading ? (
                            <p className="px-3 py-2 text-xs text-ink-muted">
                              {t("venue.searchingAddress")}
                            </p>
                          ) : addressSuggestError ? (
                            <p className="px-3 py-2 text-xs text-red-500">
                              {addressSuggestError}
                            </p>
                          ) : addressSuggestions.length > 0 ? (
                            <div className="max-h-56 overflow-y-auto py-1">
                              {addressSuggestions.map((suggestion) => (
                                <button
                                  key={suggestion.id}
                                  type="button"
                                  onClick={() => {
                                    void handleSelectAddressSuggestion(
                                      suggestion
                                    );
                                  }}
                                  className="block w-full px-3 py-2 text-left text-xs text-ink hover:bg-surface-hover"
                                >
                                  {suggestion.display}
                                </button>
                              ))}
                            </div>
                          ) : hasVietmapServiceKey ? (
                            <p className="px-3 py-2 text-xs text-ink-muted">
                              {t("venue.noSuggestions")}
                            </p>
                          ) : (
                            <p className="px-3 py-2 text-xs text-amber-600 dark:text-amber-300">
                              {t("venue.missingServiceKey")}
                            </p>
                          )}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                    {t("venue.descriptionLabel")}
                  </label>
                  <input
                    type="text"
                    value={venueForm.description}
                    onChange={(e) =>
                      setVenueForm((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    placeholder={t("venue.descriptionPlaceholder")}
                    className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none placeholder:text-gray-400 focus:border-brand-500"
                  />
                </div>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                      {t("venue.latitude")}
                    </label>
                    <input
                      type="number"
                      step="0.000001"
                      value={venueForm.latitude}
                      onChange={(e) =>
                        setVenueForm((prev) => ({
                          ...prev,
                          latitude: Number(e.target.value) || prev.latitude,
                        }))
                      }
                      className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-brand-500"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                      {t("venue.longitude")}
                    </label>
                    <input
                      type="number"
                      step="0.000001"
                      value={venueForm.longitude}
                      onChange={(e) =>
                        setVenueForm((prev) => ({
                          ...prev,
                          longitude: Number(e.target.value) || prev.longitude,
                        }))
                      }
                      className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-brand-500"
                    />
                  </div>
                </div>

                <VenueMapPicker
                  latitude={venueForm.latitude}
                  longitude={venueForm.longitude}
                  onPick={(lat, lng) =>
                    setVenueForm((prev) => ({
                      ...prev,
                      latitude: lat,
                      longitude: lng,
                    }))
                  }
                />

                <div className="flex justify-end">
                  <ActionButton
                    variant="primary"
                    onClick={handleCreateVenue}
                    disabled={creatingVenue}
                    className="rounded-lg"
                  >
                    {creatingVenue
                      ? t("classForm.creating")
                      : t("venue.createAndSelect")}
                  </ActionButton>
                </div>
              </div>
            )}
          </div>

          <ParticipantPickerModal
            pickerType={participantPicker}
            onClose={closeParticipantPicker}
            teacherSearch={teacherSearch}
            trainerSearch={trainerSearch}
            setTeacherSearch={setTeacherSearch}
            setTrainerSearch={setTrainerSearch}
            filteredManagerOptions={filteredManagerOptions}
            filteredTrainerOptions={filteredTrainerOptions}
            sessionTeacherIds={sessionTeacherIds}
            sessionTrainerIds={sessionTrainerIds}
            setSessionTeacherIds={setSessionTeacherIds}
            setSessionTrainerIds={setSessionTrainerIds}
            managerOverlapMap={managerOverlapMap}
            trainerOverlapMap={trainerOverlapMap}
          />

          <div className="flex justify-end gap-3 pt-1">
            {isEdit && (
              <ActionButton
                variant="danger"
                onClick={handleDeleteSession}
                disabled={saving}
                startIcon={<MdDelete className="h-4 w-4" />}
                className="mr-auto rounded-lg"
              >
                {t("sessionEditor.deleteSession")}
              </ActionButton>
            )}
            <ActionButton
              variant="secondary"
              onClick={() =>
                navigate(
                  isEdit
                    ? `/admin/classes-mgmt/${id}/sessions/${sessionId}`
                    : `/admin/classes-mgmt/${id}`
                )
              }
              className="rounded-lg border-line text-ink"
            >
              {t("action.cancel")}
            </ActionButton>
            <ActionButton
              type="submit"
              variant="primary"
              disabled={saving}
              className="rounded-lg"
            >
              {saving
                ? t("action.saving")
                : isEdit
                ? t("sessionEditor.saveChanges")
                : t("sessionEditor.createSubmit")}
            </ActionButton>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default SessionEditor;
